import React, { useState, useEffect } from "react";
import {
  getReceptionist,
  resetPassword,
  changeManagerStatus
} from "../utils/helperFunctions";
import { toast } from "react-toastify";
import { MdEdit, MdAdd, MdLockReset } from "react-icons/md";
import { motion } from "framer-motion";
import ReceptionistFormModal from "../components/ReceptionistFormModal";

const ReceptionistList = ({role}) => {
  const [receptionists, setReceptionists] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentReceptionist, setCurrentReceptionist] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedReceptionist, setSelectedReceptionist] = useState(null);

  const fetchReceptionists = async () => {
    setIsRefreshing(true);
    try {
      const response = await getReceptionist();
      setReceptionists(response.data || []);
    } catch (error) {
      console.error("Failed to fetch receptionists:", error);
      // toast.error("Failed to fetch receptionists");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchReceptionists();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {

      if (event.key === 'Escape' && showResetModal) {
        event.preventDefault();
        setShowResetModal(false)
      } else if (event.key === 'Enter' && showResetModal) {
        event.preventDefault();
        setShowResetModal(false);
        handleResetPassword(selectedReceptionist._id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showResetModal]);

  const toggleReceptionistStatus = async (receptionist, newStatus) => {
    try {
      const response = await changeManagerStatus(receptionist._id, newStatus);
      if (response.data.status) {
        toast.success(response.data.msg ||`Receptionist ${newStatus ? "activated" : "deactivated"} successfully`);
        fetchReceptionists();
      }
    } catch (error) {
      // console.error("Error updating receptionist status:", error);
      toast.error(error.response?.data?.msg || "Failed to update receptionist status");
    }
  };

  const openEditModal = (receptionist) => {
    setIsEditing(true);
    setCurrentReceptionist(receptionist);
    setShowModal(true);
  };

  const openAddModal = () => {
    setIsEditing(false);
    setCurrentReceptionist(null);
    setShowModal(true);
  };

  const handleResetPassword = async (receptionistId) => {
    try {
      const response = await resetPassword(receptionistId)
      toast.success(response.data.msg || "Password reset successfully");
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error(error.response?.data?.msg || "Failed to reset password");
    }
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50">
      <div className="container mx-auto bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 md:p-6 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-blue-50">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Staff Management</h1>
              <p className="text-sm text-gray-600 mt-1">Manage all user accounts and details</p>
            </div>
            <div className="flex items-center gap-3">
              
             {role !== "view only" && ( <button
                onClick={openAddModal}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <MdAdd className="h-5 w-5" />
                Add User
              </button>)}
            </div>
          </div>
        </div>
        <div className="mx-auto bg-white shadow-sm overflow-hidden">
          <div className="p-4 md:p-6">
            {isRefreshing && receptionists.length === 0 ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
              </div>
            ) : receptionists.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-16 w-16 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <p className="mt-4 text-lg font-medium text-gray-500">No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-indigo-50 to-blue-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Mobile
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Status
                      </th>
                      {role !== "view only" &&(<th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Actions
                      </th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {receptionists.map((receptionist, index) => (
                      <motion.tr
                        key={receptionist._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={`transition-colors duration-150 ${index % 2 === 0 ? "bg-white" : "bg-indigo-50/30"
                          } hover:bg-indigo-100/30`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {receptionist.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {receptionist.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {receptionist.mobile}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {receptionist.role}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => toggleReceptionistStatus(receptionist, !receptionist.isActive)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${receptionist.isActive ? "bg-indigo-600" : "bg-gray-200"
                              }`}
                              disabled={role === "view only"}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${receptionist.isActive ? "translate-x-6" : "translate-x-1"
                                }`}
                            />
                          </button>
                        </td>
                       {role !== "view only" && ( 
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openEditModal(receptionist)}
                              className="text-indigo-600 hover:text-indigo-900 p-1"
                              title="Edit"
                            >
                              <MdEdit className="h-6 w-6" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                setSelectedReceptionist(receptionist);
                                setShowResetModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Reset Password"
                            >
                              <MdLockReset className="h-6 w-6" />
                            </button>
                          </div>
                        </td>)}
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        {showModal && (
          <ReceptionistFormModal
            showModal={showModal}
            setShowModal={setShowModal}
            isEditing={isEditing}
            fetchReceptionists={fetchReceptionists}
            currentReceptionist={currentReceptionist}
          />
        )}
       
        {showResetModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setShowResetModal(false);
                setSelectedReceptionist(null);
              }}
            ></div>

            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-yellow-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>

                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      Reset Password Confirmation
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        You are about to reset the password for <span className="font-medium text-gray-900">{selectedReceptionist?.name}</span>.
                      </p>
                      <p className="mt-2 text-sm text-gray-500">
                        The password will be set to: <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">Abc@123</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={() => {
                    handleResetPassword(selectedReceptionist._id);
                    setShowResetModal(false);
                  }}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <svg
                    className="-ml-1 mr-2 h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Confirm Reset
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowResetModal(false);
                    setSelectedReceptionist(null);
                  }}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <svg
                    className="-ml-1 mr-2 h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceptionistList;

 